import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '../lib/api'
import type { SessionSummary } from '../lib/api'
import { Empty, SessionRow, Spinner } from '../components/bits'

const PAGE_SIZE = 50

export function Sessions() {
  const [page, setPage] = useState(0)
  const offset = page * PAGE_SIZE

  const q = useQuery({
    queryKey: ['sessions', 'all', page],
    queryFn: () => api.get<{ total: number; sessions: SessionSummary[] }>(
      `/api/sessions?limit=${PAGE_SIZE}&offset=${offset}`),
    // Keep the current page on screen while the next one loads, so paging
    // doesn't flash an empty list every click.
    placeholderData: keepPreviousData,
  })

  if (q.isLoading) return <div className="page"><Spinner /></div>

  const rows = q.data?.sessions ?? []
  const total = q.data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const from = total === 0 ? 0 : offset + 1
  const to = Math.min(offset + PAGE_SIZE, total)

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Sessions</h1>
          <div className="sub">{total} sessions downloaded from Race Studio</div>
        </div>
      </div>

      {rows.length === 0 ? (
        <Empty title="No sessions yet"
          body="The trackside agent uploads these automatically after Race Studio downloads a session. Nothing to do here until then." />
      ) : (
        <>
          <div className="card">{rows.map((s) => <SessionRow key={s.id} s={s} />)}</div>

          {pages > 1 ? (
            <div className="between" style={{ marginTop: 14, alignItems: 'center' }}>
              <span className="small muted">
                Showing {from}–{to} of {total}
              </span>
              <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                <button className="btn ghost" disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}>
                  <ChevronLeft size={16} /> Prev
                </button>
                <span className="small muted" style={{ minWidth: 84, textAlign: 'center' }}>
                  Page {page + 1} of {pages}
                </span>
                <button className="btn ghost" disabled={page >= pages - 1}
                  onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}>
                  Next <ChevronRight size={16} />
                </button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
